import { Box, Grid2, Typography } from "@mui/material";
import {
  CREW_ROLE_LABELS,
  CREW_ROLE_TILE_TONES,
  CREW_ROLES_WITH_REQUIRED_PAY_RATES,
  SUMMARY_TILE_TONES,
} from "../../../../constants";
import { PayRate, SummaryTileTone } from "../../../../types";
import { formatCurrency } from "../../../../utils";

const getAverage = (rates: (number | null)[]) => {
  const setRates = rates.filter((rate): rate is number => rate !== null);

  if (setRates.length === 0) return null;

  return (
    Math.round(
      (setRates.reduce((total, rate) => total + rate, 0) / setRates.length) *
        100,
    ) / 100
  );
};

type SummaryCardProps = {
  label: string;
  payRates: PayRate[];
  tone: SummaryTileTone;
};

const SummaryCard = ({ label, payRates, tone }: SummaryCardProps) => {
  const averageRate = getAverage(payRates.map((payRate) => payRate.baseRate));
  const overtimeRate = getAverage(
    payRates.map((payRate) => payRate.overtimeRate),
  );
  const setRateCount = payRates.filter(
    (payRate) => payRate.baseRate !== null,
  ).length;

  return (
    <Box
      className="dashboard-tile"
      sx={{
        minHeight: "auto",
        py: 1.5,
        backgroundColor: SUMMARY_TILE_TONES[tone].background,
        borderColor: SUMMARY_TILE_TONES[tone].border,
      }}
    >
      <Typography className="dashboard-metric-label">{label}</Typography>
      <Typography
        className="dashboard-kpi-value"
        sx={{ mt: 0.5, color: SUMMARY_TILE_TONES[tone].value }}
      >
        {averageRate === null ? "—" : formatCurrency(averageRate)}
      </Typography>
      <Typography className="dashboard-helper-text" sx={{ mt: 0.5 }}>
        {averageRate === null
          ? "Not set yet"
          : `${setRateCount > 1 ? "avg " : ""}base / hr${overtimeRate === null ? "" : ` · OT ${formatCurrency(overtimeRate)}`}`}
      </Typography>
    </Box>
  );
};

type PayRateSummaryCardsProps = {
  payRates: PayRate[];
};

export const PayRateSummaryCards = ({ payRates }: PayRateSummaryCardsProps) => {
  const cards: SummaryCardProps[] = [
    { label: "All Crew", payRates, tone: "neutral" },
    ...CREW_ROLES_WITH_REQUIRED_PAY_RATES.map((role) => ({
      label: CREW_ROLE_LABELS[role],
      payRates: payRates.filter((payRate) => payRate.roleCode === role),
      tone: CREW_ROLE_TILE_TONES[role] ?? "neutral",
    })),
  ];

  return (
    <Grid2 container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card) => (
        <Grid2 key={card.label} size={{ xs: 12, md: 4 }}>
          <SummaryCard {...card} />
        </Grid2>
      ))}
    </Grid2>
  );
};
